import React from "react";
import { useAuth } from "@/hooks/useAuth";
import { formatFullName } from "@/helpers";
import {
  dashboardService,
  type AdminDashboardData,
} from "@/services/api/dashboard";
import {
  AgentPerformance,
  DashboardErrorState,
  DashboardLoadingState,
  MetricCard,
  QuickActions,
  RecentActivity,
  UpcomingAppointments,
} from "./dashboard";

const getDashboardErrorMessage = (error: unknown) => {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Unable to load dashboard report. Please try again.";
};

const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const fullName = formatFullName(user as any);
  const displayName = fullName || user?.email || "Admin Testing";
  const [dashboardData, setDashboardData] =
    React.useState<AdminDashboardData | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const isMountedRef = React.useRef(false);

  const fetchDashboardReport = React.useCallback(async () => {
    setIsLoading(true);
    setErrorMessage(null);

    try {
      const response = await dashboardService.getReport();

      if (!response?.data) {
        throw new Error("Dashboard report response did not include data.");
      }

      if (isMountedRef.current) {
        setDashboardData(response.data);
      }
    } catch (error) {
      if (isMountedRef.current) {
        setErrorMessage(getDashboardErrorMessage(error));
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  React.useEffect(() => {
    isMountedRef.current = true;
    void fetchDashboardReport();

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchDashboardReport]);

  if (isLoading && !dashboardData) {
    return <DashboardLoadingState />;
  }

  if (errorMessage && !dashboardData) {
    return (
      <DashboardErrorState
        message={errorMessage}
        onRetry={() => {
          void fetchDashboardReport();
        }}
      />
    );
  }

  if (!dashboardData) {
    return null;
  }

  return (
    <div
      aria-label={dashboardData.reportMetadata.reportName}
      className="space-y-5 pb-2"
    >
      <header className="relative flex min-h-[92px] items-center overflow-hidden rounded-lg bg-[#0b5f9f] px-7 py-5 shadow-md">
        <div
          className="absolute inset-y-0 right-0 w-[34%] bg-yellow-400"
          style={{ clipPath: "polygon(35% 0, 100% 0, 100% 100%, 0 100%)" }}
        />
        <div className="relative z-10">
          <h1 className="m-0 text-2xl font-bold leading-tight text-white md:text-3xl">
            Welcome back,{" "}
            <span className="text-yellow-300">{displayName}</span>
          </h1>
          <p className="mt-2 text-sm font-semibold text-white">
            Logged in as: <span>Admin</span>
          </p>
        </div>
      </header>

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {dashboardData.dashboardMetrics.map((stat) => (
          <MetricCard key={stat.metricId} stat={stat} />
        ))}
      </section>

      <section className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(380px,0.95fr)]">
        <AgentPerformance performance={dashboardData.agentPerformance} />
        <RecentActivity activities={dashboardData.recentActivities} />
        <UpcomingAppointments
          appointments={dashboardData.upcomingAppointments}
        />
        <QuickActions actions={dashboardData.quickActions} />
      </section>
    </div>
  );
};

export default AdminDashboard;
